import { motion } from 'framer-motion'
import styled, { css, keyframes } from 'styled-components'

// Customer-facing screen, read from ~1 m away. Sizes scale with vmin so the
// layout keeps its proportions on 1920x1080, 1280x800 and on rotated
// portrait panels (where vmin is still the short side).

// Neutrals and status colors come from the global CSS variables (src/style.js)
const TEXT = 'var(--color-text)'
const TEXT_SECONDARY = 'var(--color-muted)'
const HAIRLINE = 'var(--color-line)'
const SUCCESS = 'var(--color-success)'
const SUCCESS_BG = 'var(--color-success-soft)'

const tabular = css`
  font-variant-numeric: tabular-nums;
`

export const Wrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.body};
  font-family: ${({ theme }) => theme.font};
  color: ${TEXT};
  overflow: hidden;

  /* Force landscape on portrait secondary displays (e.g. Elo HDMI) */
  @media (orientation: portrait) {
    width: 100vh;
    height: 100vw;
    transform: rotate(-90deg) translateX(-100%);
    transform-origin: top left;
  }
`

export const LogoBar = styled.header`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  height: clamp(72px, 8.5vmin, 96px);
  background: ${({ theme }) => theme.colors.global};

  img {
    height: clamp(36px, 4.4vmin, 48px);
    display: block;
  }
`

// Shared centered layout (welcome / payment / thank-you)

export const CenterContent = styled.main`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4vmin;
  text-align: center;
`

export const IconBadge = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: clamp(120px, 16vmin, 176px);
  height: clamp(120px, 16vmin, 176px);
  margin-bottom: 4vmin;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.primary}1A;
  color: ${({ theme }) => theme.colors.primaryText || theme.colors.primary};

  svg {
    width: 46%;
    height: 46%;
  }
`

export const Headline = styled.h1`
  margin: 0 0 1.6vmin;
  font-size: clamp(40px, 6vmin, 68px);
  font-weight: 700;
  line-height: 1.1;
  letter-spacing: -0.02em;
  color: ${TEXT};
`

export const Subline = styled.p`
  margin: 0;
  max-width: 80vmin;
  font-size: clamp(20px, 2.8vmin, 30px);
  line-height: 1.35;
  color: ${TEXT_SECONDARY};
`

// Cart view

export const CartLayout = styled.main`
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr) clamp(340px, 44vmin, 600px);
  gap: 3vmin;
  padding: 3vmin;
`

export const Panel = styled.section`
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border: 1px solid ${HAIRLINE};
  border-radius: var(--radius-card);
  overflow: hidden;
`

export const ItemsHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
  flex-shrink: 0;
  padding: 2.6vmin 3vmin 2vmin;
  border-bottom: 1px solid ${HAIRLINE};
`

export const ItemsTitle = styled.h2`
  margin: 0;
  font-size: clamp(22px, 3vmin, 32px);
  font-weight: 700;
  color: ${TEXT};
`

export const ItemCount = styled.span`
  padding: 6px 16px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.body};
  font-size: clamp(16px, 2vmin, 22px);
  font-weight: 600;
  color: ${TEXT};
  white-space: nowrap;
  ${tabular}
`

export const ItemsList = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0 3vmin;
  /* soften rows scrolling under the header */
  -webkit-mask-image: linear-gradient(to bottom, transparent 0, #000 2.4vmin);
  mask-image: linear-gradient(to bottom, transparent 0, #000 2.4vmin);

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: ${HAIRLINE}; border-radius: 2px; }
`

export const CartItemRow = styled.div`
  display: flex;
  align-items: center;
  gap: 2.4vmin;
  padding: 2vmin 0;
  border-bottom: 1px solid ${HAIRLINE};
`

export const Thumb = styled.div`
  flex-shrink: 0;
  width: clamp(56px, 7.4vmin, 80px);
  height: clamp(56px, 7.4vmin, 80px);
  padding: 6px;
  border: 1px solid ${HAIRLINE};
  border-radius: 12px;
  background: #ffffff;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
  }
`

export const CartItemInfo = styled.div`
  flex: 1;
  min-width: 0;
`

export const CartItemName = styled.div`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  margin-bottom: 0.6vmin;
  font-size: clamp(20px, 2.6vmin, 28px);
  font-weight: 600;
  line-height: 1.25;
  color: ${TEXT};
`

export const CartItemQty = styled.div`
  font-size: clamp(16px, 2vmin, 22px);
  color: ${TEXT_SECONDARY};
  ${tabular}
`

export const CartItemTotal = styled.div`
  flex-shrink: 0;
  font-size: clamp(22px, 2.8vmin, 30px);
  font-weight: 700;
  color: ${TEXT};
  ${tabular}
`

// Side column: latest item + total

export const SideColumn = styled.div`
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 3vmin;
`

export const LastItemPanel = styled(Panel)`
  flex: 1;
  padding: 3vmin;
`

export const LastItemContent = styled(motion.div)`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
`

export const LastItemImage = styled.div`
  flex: 1;
  min-height: 0;
  width: 100%;
  max-height: 32vmin;
  margin-bottom: 2.4vmin;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
  }
`

export const LastItemName = styled.div`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  margin-bottom: 0.8vmin;
  font-size: clamp(18px, 2.4vmin, 26px);
  font-weight: 600;
  line-height: 1.25;
  text-wrap: balance;
  color: ${TEXT};
`

export const LastItemPrice = styled.div`
  font-size: clamp(16px, 2vmin, 22px);
  color: ${TEXT_SECONDARY};
  ${tabular}
`

export const SummaryPanel = styled(Panel)`
  flex-shrink: 0;
  padding: 3.4vmin;
  container-type: inline-size;
`

export const LoyaltyRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 3vmin;
  padding: 1.6vmin 2vmin;
  border-radius: 12px;
  background: ${SUCCESS_BG};
  color: ${SUCCESS};
  font-size: clamp(16px, 2vmin, 22px);
  font-weight: 600;
  line-height: 1.3;
`

export const LoyaltyLabel = styled.span`
  color: ${TEXT};
`

export const DiscountAmount = styled.span`
  flex-shrink: 0;
  font-size: clamp(20px, 2.6vmin, 28px);
  font-weight: 700;
  ${tabular}
`

export const TotalBlock = styled.div`
  ${({ $divider }) => $divider && css`
    padding-top: 3vmin;
    border-top: 1px solid ${HAIRLINE};
  `}
`

export const TotalLabel = styled.div`
  margin-bottom: 0.8vmin;
  font-size: clamp(20px, 2.8vmin, 30px);
  font-weight: 600;
  color: ${TEXT_SECONDARY};
`

export const TotalAmount = styled.div`
  font-size: clamp(44px, 7vmin, 88px);
  /* fit long totals (e.g. "1234,56 zł") to the panel width */
  font-size: min(112px, 19cqw);
  font-weight: 800;
  line-height: 1.05;
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.colors.global};
  white-space: nowrap;
  ${tabular}
`

export const Currency = styled.span`
  font-size: 0.55em;
  font-weight: 700;
  margin-right: 0.08em;
`

// Payment processing

const spin = keyframes`
  to { transform: rotate(360deg); }
`

export const ProcessingRing = styled.span`
  position: absolute;
  inset: -12px;
  border-radius: 50%;
  border: 4px solid ${({ theme }) => theme.colors.primary}1F;
  border-top-color: ${({ theme }) => theme.colors.primary};
  animation: ${spin} 1s linear infinite;
`

export const Amount = styled.div`
  margin: 1vmin 0 4vmin;
  font-size: clamp(56px, 9.4vmin, 108px);
  font-weight: 800;
  line-height: 1.05;
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.colors.global};
  ${tabular}
`

export const HintPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 14px;
  max-width: 90vmin;
  padding: 1.8vmin 3.2vmin;
  border: 1px solid ${HAIRLINE};
  border-radius: 999px;
  background: #ffffff;
  font-size: clamp(20px, 2.8vmin, 30px);
  font-weight: 500;
  color: ${TEXT};

  svg {
    flex-shrink: 0;
    width: 1.2em;
    height: 1.2em;
    color: ${({ theme }) => theme.colors.primaryText || theme.colors.primary};
  }
`

// Thank-you

export const SuccessBadge = styled(IconBadge)`
  background: ${({ theme }) => theme.colors.primary};
  color: #ffffff;

  svg {
    width: 76%;
    height: 76%;
  }
`

export const PaidAmount = styled.div`
  margin-top: 3vmin;
  padding: 1.2vmin 3vmin;
  border-radius: 999px;
  background: #ffffff;
  border: 1px solid ${HAIRLINE};
  font-size: clamp(24px, 3.4vmin, 38px);
  font-weight: 700;
  color: ${TEXT};
  ${tabular}
`
